<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowPathIcon, ArrowsRightLeftIcon, ChevronRightIcon, ClockIcon, InformationCircleIcon, MagnifyingGlassIcon,
  XMarkIcon } from '@heroicons/vue/24/outline'
import Pts2ControllerNav from '../components/pts2/Pts2ControllerNav.vue'
import Pts2DiagnosticsStrip from '../components/pts2/Pts2DiagnosticsStrip.vue'
import { getControllerSummary } from '../pts2/controllers.js'
import { getConfigurationObservation, listConfigurationHistory } from '../pts2/configurationHistory.js'
import { defaultHistoryRange, parseHistoryRange } from '../pts2/controllerTabPresentation.js'
import '../pts2/controllers.css'

const route = useRoute()
const controllerId = computed(() => Number(route.params.controllerId))
const controller = ref(null)
const page = ref({ items: [], nextCursor: null, hasMore: false })
const selected = ref(null)
const loading = ref(true)
const loadingDetail = ref(false)
const error = ref('')
const section = ref('')
const source = ref('ALL')
const query = ref('')
const activeTab = ref('ALL')
const dateRange = ref(defaultHistoryRange())
const loadingMore = ref(false)
let request

const stationId = computed(() => controller.value?.stationId ?? null)
const stationName = computed(() => controller.value?.stationDisplayName ?? `АЗС ${stationId.value ?? '—'}`)
const visibleItems = computed(() => {
  const needle = query.value.trim().toLowerCase()
  return page.value.items.filter((item) => (!needle || item.configurationId.toLowerCase().includes(needle))
    && (source.value === 'ALL' || source.value === item.source))
})
const differences = computed(() => selected.value?.diff?.items ?? [])
const tabs = computed(() => {
  const counts = new Map()
  for (const item of differences.value) counts.set(item.section, (counts.get(item.section) ?? 0) + 1)
  return [{ key: 'ALL', label: 'Усі зміни', count: differences.value.length },
    ...[...counts.entries()].map(([key, count]) => ({ key, label: sectionLabel(key), count }))]
})
const visibleDifferences = computed(() => activeTab.value === 'ALL' ? differences.value
  : differences.value.filter((item) => item.section === activeTab.value))

function formatDate(value) {
  const date = value ? new Date(value) : null
  return date && Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('uk-UA', { dateStyle: 'short', timeStyle: 'short' }).format(date) : '—'
}

function sectionLabel(value) {
  return ({ Pumps: 'Пристрої', PumpNozzles: 'Пристрої', FuelGrades: 'Ціни',
    OperatorTags: 'Операторські теги', TagsList: 'Операторські теги', Probes: 'Рівнеміри',
    PriceBoards: 'Цінові табло', Readers: 'RFID-зчитувачі', Sensors: 'Датчики', Tanks: 'Резервуари',
    MeasurementUnits: 'Одиниці вимірювання', ControllerType: 'Тип контролера', UsersConfiguration: 'Користувачі',
    RemoteServerConfiguration: 'Віддалений сервер', PtsNetworkSettings: 'Мережа контролера', ReadersConfiguration: 'RFID-зчитувачі' })[value] ?? value
}

function receiptLabel(value) {
  return ({ BASELINE: 'Базовий стан', RETRANSMISSION: 'Повторна доставка',
    OBSERVED_CHANGE: 'Виявлена зміна', CONFIRMATION: 'Підтвердження' })[value] ?? value
}

function completeness(item) {
  const values = Object.values(item.completeness ?? {})
  if (!values.length) return '—'
  const complete = values.filter((value) => value === 'COMPLETE').length
  return complete === values.length ? '100%' : `${Math.round(complete / values.length * 100)}%`
}

function displayValue(value) {
  if (value == null) return '—'
  if (Array.isArray(value)) return value.length ? `ПРК ${value.join(', ')}` : 'Без доступу'
  if (typeof value === 'boolean') return value ? 'Активний' : 'Заблокований'
  return String(value)
}

function selectedRange() { return parseHistoryRange(dateRange.value) }

function applyDateRange() {
  if (!selectedRange()) {
    error.value = 'Вкажіть коректний період у форматі ДД.ММ.РРРР - ДД.ММ.РРРР.'
    return
  }
  load()
}

async function selectObservation(item) {
  if (!stationId.value || loadingDetail.value) return
  loadingDetail.value = true
  error.value = ''
  try {
    selected.value = await getConfigurationObservation(stationId.value, controllerId.value, item.id,
      { signal: request?.signal })
    activeTab.value = 'ALL'
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = 'Не вдалося завантажити порівняння конфігурації.'
  } finally {
    loadingDetail.value = false
  }
}

async function load() {
  request?.abort()
  request = new AbortController()
  loading.value = true
  error.value = ''
  selected.value = null
  try {
    controller.value = await getControllerSummary(controllerId.value, { signal: request.signal })
    const range = selectedRange()
    if (!range) throw new Error('invalid_date_range')
    page.value = await listConfigurationHistory(controller.value.stationId, controllerId.value,
      { ...range, section: section.value || undefined, limit: 50, signal: request.signal })
    if (page.value.items[0]) await selectObservation(page.value.items[0])
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = 'Історія конфігурації недоступна. Перевірте доступ і повторіть спробу.'
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!page.value.hasMore || !page.value.nextCursor || loadingMore.value) return
  loadingMore.value = true
  error.value = ''
  try {
    const next = await listConfigurationHistory(stationId.value, controllerId.value,
      { ...selectedRange(), section: section.value || undefined, cursor: page.value.nextCursor, limit: 50,
        signal: request?.signal })
    page.value = { ...next, items: [...page.value.items, ...next.items] }
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = 'Не вдалося завантажити наступну сторінку історії.'
  } finally {
    loadingMore.value = false
  }
}

watch(controllerId, load)
watch(section, load)
onMounted(load)
onBeforeUnmount(() => request?.abort())
</script>

<template>
  <section class="pc-page pc-detail pts2-history-page" aria-labelledby="pts2-history-title">
    <nav class="pc-breadcrumb" aria-label="Навігаційний шлях">
      <RouterLink :to="{ name: 'pts2-controllers' }">Контролери</RouterLink>
      <ChevronRightIcon aria-hidden="true" />
      <span>{{ controller?.displayName ?? 'Історія конфігурації' }}</span>
    </nav>

    <div v-if="loading" class="pc-table-loading" role="status">Завантажуємо історію конфігурації…</div>
    <template v-else-if="controller">
      <header class="pc-heading pc-detail-heading pts2-page-heading">
        <div>
          <div class="pts2-heading-line">
            <h1 id="pts2-history-title">{{ controller.displayName }}</h1>
            <span class="pc-state" :class="controller.connectionFreshness === 'FRESH' ? 'fresh' : 'unknown'"><i></i>{{ controller.connectionFreshness === 'FRESH' ? 'Онлайн' : 'Стан невідомий' }}</span>
          </div>
          <p>{{ controller.stableCode }} · {{ stationName }} · Останні дані: {{ formatDate(controller.lastSeenAt) }}</p>
        </div>
        <div class="pc-heading-actions">
          <button class="pc-secondary" type="button" @click="load"><ArrowPathIcon aria-hidden="true" />Оновити</button>
        </div>
      </header>
      <p v-if="error" class="pc-notice" role="alert">{{ error }}</p>
      <Pts2DiagnosticsStrip :snapshot="controller.diagnostics" :summary="controller" :failed="false" />
      <Pts2ControllerNav :controller-id="controllerId" active="history" />

      <section class="pts2-history-filters" aria-label="Фільтри історії">
        <label><ClockIcon aria-hidden="true" /><span class="sr-only">Період</span><input v-model="dateRange" maxlength="23" inputmode="numeric" aria-label="Період" @blur="applyDateRange" /></label>
        <select v-model="section" aria-label="Розділ"><option value="">Усі розділи</option><option value="Pumps">Пристрої</option><option value="FuelGrades">Ціни</option><option value="TagsList">Операторські теги</option></select>
        <select v-model="source" aria-label="Джерело"><option value="ALL">Усі джерела</option><option value="CONTROLLER_OBSERVED">Контролер</option></select>
        <label class="pts2-search"><MagnifyingGlassIcon aria-hidden="true" /><input v-model="query" placeholder="ID конфігурації" /></label>
      </section>

      <section class="pts2-surface pts2-history-list">
        <header><h2>Історія конфігурації</h2><span>Показано: {{ visibleItems.length }}</span></header>
        <div class="pts2-table-scroll">
          <table>
            <thead><tr><th>Час спостереження</th><th>Джерело</th><th>ID конфігурації</th><th>Повнота</th><th>Змін</th><th>Стан</th></tr></thead>
            <tbody>
              <tr v-for="item in visibleItems" :key="item.id" :class="{ selected: selected?.id === item.id }" @click="selectObservation(item)">
                <td><strong>{{ formatDate(item.observedAt) }}</strong></td><td>Виявлено сервером</td><td>{{ item.configurationId }}</td>
                <td>{{ completeness(item) }}</td><td>{{ item.changeCount ?? '—' }}</td>
                <td><span class="pts2-history-badge" :class="item.receiptKind.toLowerCase()">{{ receiptLabel(item.receiptKind) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="!visibleItems.length" class="pc-table-empty">За вибраними фільтрами записів немає.</p>
        <button v-if="page.hasMore" class="pc-secondary pts2-more" type="button" :disabled="loadingMore" @click="loadMore">{{ loadingMore ? 'Завантажуємо…' : 'Показати ще' }}</button>
      </section>

      <section v-if="selected" class="pts2-surface pts2-history-compare">
        <header><div><h2>Порівняння конфігурацій</h2><span class="pts2-history-badge observed_change">{{ receiptLabel(selected.receiptKind) }}</span></div><button type="button" class="pts2-icon-button" aria-label="Закрити порівняння" @click="selected = null"><XMarkIcon /></button></header>
        <div class="pts2-compare-summary">
          <div><small>Поточна конфігурація</small><strong>{{ formatDate(selected.observedAt) }}</strong><span>ID: {{ selected.configurationId }}</span><span>Джерело: Виявлено сервером</span><span>Повнота: {{ completeness(selected) }}</span></div>
          <div class="pts2-swap" aria-hidden="true"><ArrowsRightLeftIcon /></div>
          <div><small>Попередня конфігурація</small><strong>{{ selected.previousObservationId ? 'Попереднє спостереження' : 'Недоступна' }}</strong><span>ID: {{ selected.previousObservationId ?? '—' }}</span><span>Джерело: Виявлено сервером</span></div>
          <p class="pts2-compare-note"><InformationCircleIcon aria-hidden="true" />{{ selected.diffState === 'PREVIOUS_UNAVAILABLE'
            ? 'Попередня конфігурація недоступна. Сервер не вигадує відсутні зміни.'
            : 'Відсутність розділу в конфігурації не є підтвердженим видаленням. Це може бути неповна доставка або відсутність змін у розділі.' }}</p>
        </div>
        <nav class="pts2-compare-tabs" aria-label="Розділи порівняння"><button v-for="tab in tabs" :key="tab.key" type="button" :class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">{{ tab.label }} <span>{{ tab.count }}</span></button></nav>
        <div v-if="loadingDetail" class="pc-table-loading">Завантажуємо порівняння…</div>
        <div v-else class="pts2-table-scroll">
          <table><thead><tr><th>Розділ</th><th>Параметр</th><th>Було</th><th>Стало</th><th>Примітка</th></tr></thead>
            <tbody><tr v-for="item in visibleDifferences" :key="`${item.section}:${item.deviceId}:${item.field}:${item.operationId}`">
              <td>{{ sectionLabel(item.section) }}</td><td>{{ item.field }}</td><td>{{ displayValue(item.oldValue) }}</td><td class="pts2-new-value">{{ displayValue(item.newValue) }}</td>
              <td>{{ item.source === 'SERVER_OPERATION_VERIFIED' ? `Підтверджено сервером · ${item.actor.username}` : item.redacted ? 'Значення приховано' : 'Спостережено контролером' }}</td>
            </tr></tbody>
          </table>
          <p v-if="!visibleDifferences.length" class="pc-table-empty">Підтверджених змін між цими спостереженнями немає.</p>
        </div>
      </section>
    </template>
  </section>
</template>
